import type { GuestsApiSearch } from 'restaurantAdmin/components/guestAutocomplete/apiHelpers';
import { ControlledGuestAutocomplete } from 'restaurantAdmin/components/guestAutocomplete/ControlledGuestAutocomplete';
import { AssignedServer } from 'restaurantAdmin/components/server/AssignedServer';
import {
  ServicePageModalType,
  useServicePageModalContext,
} from 'restaurantAdmin/context/ServicePageModalContext';
import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import { type AssignedServer as AssignedServerType } from 'restaurantAdmin/floorPlans/apiHelpers';
import { useReservationServiceContext } from 'restaurantAdmin/reservations/service/state/ReservationServiceContext';
import { Dialog } from '@base-ui/react';
import { Button, ButtonVariants } from '@components/button/Button';
import { ControlledFormCheckbox } from '@components/formInputs/ControlledFormCheckbox';
import { ControlledFormInput } from '@components/formInputs/ControlledFormInput';
import { LayoutVariant } from '@components/formInputs/sharedTypes';
import { GuestCountPicker } from '@components/guestCountPicker/GuestCountPicker';
import { errorToast } from '@components/toasts/Toasts';
import { reportAppError } from '@shared/reportAppError';
import cx from 'classnames';
import { useMemo } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import typography from '~styles/typography.scss';
import { SeatingSelectedTables } from '../SeatingSelectedTables';
import { useServers } from '../sidePanel/servers/hooks';
import { useReservationServiceSelectedSeatingContext } from '../state/ReservationServiceSelectedSeatingContext';
import { useSeatWalkIn, useWalkInAvailabilities } from './hooks';
import styles from './SeatWalkInFormDrawer.scss';

export interface WalkInFormData {
  guestCount: number;
  firstName: string;
  lastName: string;
  saveAsGuest: boolean;
  guestId?: string;
  serverId?: string;
}

const DEFAULT_GUEST_COUNT = 2;

export const SeatWalkInFormDrawer = () => {
  const { selectedFloorPlan } = useReservationServiceContext();
  const { openModal } = useServicePageModalContext();
  const { data: servers } = useServers();
  const { maxReservationGuests } = useRestaurant();
  const { selectedFloorPlanTables } =
    useReservationServiceSelectedSeatingContext();

  const layoutVariant = LayoutVariant.Vertical;

  const {
    control,
    getValues,
    setValue,
    handleSubmit,
    formState: { isSubmitting },
    reset,
  } = useForm<WalkInFormData>({
    defaultValues: {
      guestCount: DEFAULT_GUEST_COUNT,
      saveAsGuest: false,
      serverId: selectedFloorPlanTables[0]?.assignedServer?.id,
    },
  });

  const guestCount = useWatch({ control, name: 'guestCount' });
  const guestId = useWatch({ control, name: 'guestId' });
  const saveAsGuest = useWatch({ control, name: 'saveAsGuest' });
  const serverId = useWatch({ control, name: 'serverId' });

  const seatWalkInMutation = useSeatWalkIn({
    onSuccess: reset,
  });

  const { data: walkInAvailabilities, error: walkInAvailabilityFetchError } =
    useWalkInAvailabilities({
      floorPlanTables: selectedFloorPlanTables,
      guestCount,
    });

  const assignedServer = useMemo(
    () => servers.find((server) => server.id === serverId) || null,
    [serverId],
  );

  if (!selectedFloorPlan) return null;

  const submitWalkIn = async (data: WalkInFormData) => {
    if (walkInAvailabilityFetchError) {
      reset();
      errorToast({
        message: 'Failed to confirm seat availability. Please try again.',
      });
      reportAppError(walkInAvailabilityFetchError);
      return;
    }

    if (walkInAvailabilities.some((a) => !a.hasAvailability)) {
      openModal(ServicePageModalType.WalkInSeatingConflict, {
        handleConfirm: () => {
          void seatWalkInMutation.mutate({
            ...getValues(),
            floorPlanTables: selectedFloorPlanTables,
          });
        },
      });
      return;
    }

    seatWalkInMutation.mutate({
      ...data,
      floorPlanTables: selectedFloorPlanTables,
    });
  };

  const handleAutocompleteSelect = (newGuest: GuestsApiSearch) => {
    setValue('guestId', newGuest.id);
    setValue('firstName', newGuest.firstName);
    setValue('lastName', newGuest.lastName);
    setValue('saveAsGuest', false);
  };

  const handleAutocompleteClear = () => {
    setValue('firstName', '');
    setValue('lastName', '');
  };

  const saveAsGuestFieldsDisabled = !!guestId;

  const handleServerChange = (server: AssignedServerType | null) => {
    setValue('serverId', server?.id);
  };

  return (
    <div className={styles.container} aria-label="Seat Walk In Form">
      <header className={cx(typography.h4, styles.headerBar)}>
        Seat Walk-In
      </header>

      <div className={styles.drawerContent}>
        <AssignedServer
          occupant={{ occupantId: null, occupantType: 'walkIn' }}
          server={assignedServer}
          submitHandler={handleServerChange}
        />

        <form
          aria-label="Seat Walk-in Form"
          className={styles.walkInForm}
          onSubmit={handleSubmit(submitWalkIn)}
        >
          <Controller
            control={control}
            name="guestCount"
            render={({ field: { onChange, value } }) => (
              <GuestCountPicker
                guestCount={value}
                max={maxReservationGuests}
                onChange={onChange}
              />
            )}
          />

          <ControlledGuestAutocomplete
            control={control}
            name="guestId"
            onSelect={handleAutocompleteSelect}
            onClear={handleAutocompleteClear}
          />

          <ControlledFormInput
            className={styles.nameInput}
            control={control}
            label="First Name"
            name="firstName"
            placeholder={saveAsGuest ? 'Required' : 'Optional'}
            rules={{
              required: saveAsGuest,
            }}
            type="text"
            variant={layoutVariant}
            disabled={saveAsGuestFieldsDisabled}
          />

          <ControlledFormInput
            className={styles.nameInput}
            control={control}
            label="Last Name"
            name="lastName"
            placeholder={saveAsGuest ? 'Required' : 'Optional'}
            rules={{
              required: saveAsGuest,
            }}
            type="text"
            variant={layoutVariant}
            disabled={saveAsGuestFieldsDisabled}
          />

          <ControlledFormCheckbox
            className={styles.nameInput}
            control={control}
            label="Save to Guest Book"
            name="saveAsGuest"
            disabled={saveAsGuestFieldsDisabled}
          />

          <SeatingSelectedTables floorPlanTables={selectedFloorPlanTables} />

          <div className={styles.callToActionSection}>
            <Button
              label="Seat Walk-in"
              type="submit"
              variant={ButtonVariants.Primary}
              isDisabled={isSubmitting || selectedFloorPlanTables.length === 0}
            />

            <Dialog.Close
              render={({ onClick }) => (
                <Button
                  label="Cancel"
                  onClick={
                    onClick as React.MouseEventHandler<HTMLButtonElement>
                  }
                  variant={ButtonVariants.Tertiary}
                />
              )}
            />
          </div>
        </form>
      </div>
    </div>
  );
};
